import { customizeError, formatDNI } from '@chilecompra/react-kit'
import { makeActionCreator } from '../../config/store/utils'
import { checkIfForeignDni, isNaturalPerson } from '../../modules/utils/helpers'
import { getBasicSellerInformation, getLegalAddress, postBlockedInformation } from '../../services/seller'
import { getSellerBusinessSize } from '../../services/sii'
import { cachedDataWithExpiration } from '../../modules/utils/localstorage'
import { getEconomicActivities, postGetUserInfo } from '../../services/user'

export const GET_BASIC_SELLER_INFORMATION = 'GET_BASIC_SELLER_INFORMATION'
export const GET_BASIC_SELLER_INFORMATION_ERROR = 'GET_BASIC_SELLER_INFORMATION_ERROR'
export const GET_BASIC_SELLER_INFORMATION_SUCCESS = 'GET_BASIC_SELLER_INFORMATION_SUCCESS'
export const onGetBasicSellerInformation = makeActionCreator(GET_BASIC_SELLER_INFORMATION)
export const onGetBasicSellerInformationError = makeActionCreator(GET_BASIC_SELLER_INFORMATION_ERROR, 'payload')
export const onGetBasicSellerInformationSuccess = makeActionCreator(GET_BASIC_SELLER_INFORMATION_SUCCESS, 'payload')
export const onGetBasicSellerInformationThunk = payload => async dispatch => {
  const { dni = '' } = payload || {}

  dispatch(onGetBasicSellerInformation())

  try {
    const handleGetBasicSellerInformation = async () => {
      const isForeign = checkIfForeignDni(dni)

      const { data } = await getBasicSellerInformation({
        actionType: GET_BASIC_SELLER_INFORMATION,
        dni,
        isForeign: isForeign ? 1 : 0
      })

      const { data: dataSize } = await getSellerBusinessSize({ dni })

      // Foreign seller are marked as "legal" seller type
      const sellerType = !isForeign && isNaturalPerson(formatDNI(dni, 2)) ? 'natural' : 'legal'
      const contractStatus = parseInt(data?.payload?.contrato ?? 0, 10)

      return {
        EMTClassificationDetail: {
          isForced: dataSize?.payload?.esForzado,
          forcedReason: dataSize?.payload?.motivoForzado
        },
        abilityStatus: contractStatus === 0 ? 'SIN INFORMACIÓN' : data?.payload?.habilidadProveedor,
        accreditationEndDate: data?.payload?.fechaVencimientoContrato,
        contractStatus,
        dni: data?.payload?.rut.toUpperCase(),
        isMinorSizeSeller: dataSize?.payload?.clasificacion === 'EMT',
        orgLegalName: data?.payload?.razonSocial || data?.payload?.nombreFantasia,
        orgName: data?.payload?.nombreFantasia,
        sellerIsEliminated: data?.payload?.estadoEliminacion,
        sellerIsSuspended: data?.payload?.estadoSuspension,
        sellerType,
        suspensionReason: data?.payload?.glosaSuspension
      }
    }

    const data = await cachedDataWithExpiration({
      dni,
      expirationTime: 3600000,
      getBasicSellerInformationCallback: handleGetBasicSellerInformation,
      key: 'basicSellerInformation'
    })

    return dispatch(onGetBasicSellerInformationSuccess(data))
  } catch (error) {
    if (error?.originalError?.code === 'ERR_CANCELED') return

    if (error?.originalError?.code === 'ERR_NETWORK') {
      return dispatch(
        onGetBasicSellerInformationError({
          dni,
          error: customizeError({ originalError: error?.originalError, code: 504, reason: 'TIME_OUT_ERROR' }).toObject()
        })
      )
    }

    let reason
    switch (error.code) {
      case 401:
        reason = 'UNAUTHORIZED_ERROR'
        break
      case 404:
        reason = 'DNI_DOES_NOT_MATCH_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetBasicSellerInformationError({
        dni,
        error: customizeError({
          code: error.code,
          originalError: error?.originalError,
          reason
        }).toObject()
      })
    )
  }
}

export const GET_ECONOMIC_ACTIVITIES_INFO = 'GET_ECONOMIC_ACTIVITIES_INFO'
export const GET_ECONOMIC_ACTIVITIES_INFO_ERROR = 'GET_ECONOMIC_ACTIVITIES_INFO_ERROR'
export const GET_ECONOMIC_ACTIVITIES_INFO_SUCCESS = 'GET_ECONOMIC_ACTIVITIES_INFO_SUCCESS'
export const onGetEconomicActivitiesInfo = makeActionCreator(GET_ECONOMIC_ACTIVITIES_INFO)
export const onGetEconomicActivitiesInfoError = makeActionCreator(GET_ECONOMIC_ACTIVITIES_INFO_ERROR, 'payload')
export const onGetEconomicActivitiesInfoSuccess = makeActionCreator(GET_ECONOMIC_ACTIVITIES_INFO_SUCCESS, 'payload')
export const onGetEconomicActivitiesInfoThunk = payload => async dispatch => {
  const { dni = '' } = payload || {}

  dispatch(onGetEconomicActivitiesInfo())

  try {
    const { data } = await getEconomicActivities({ dni })

    const economicActivities = {
      economicActivity: data.payload.actividadEco,
      glosa: data.payload.glosa,
      state: data.payload.estado
    }

    dispatch(onGetEconomicActivitiesInfoSuccess(economicActivities))
  } catch (error) {
    if (error.originalError?.code === 'ERR_CANCELED') return

    dispatch(
      onGetEconomicActivitiesInfoError({
        error: customizeError({
          code: error?.code,
          originalError: error?.originalError,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const GET_SELLER_BLOCKED_INFO = 'GET_SELLER_BLOCKED_INFO'
export const GET_SELLER_BLOCKED_INFO_ERROR = 'GET_SELLER_BLOCKED_INFO_ERROR'
export const GET_SELLER_BLOCKED_INFO_SUCCESS = 'GET_SELLER_BLOCKED_INFO_SUCCESS'
export const onGetSellerBlockedInfo = makeActionCreator(GET_SELLER_BLOCKED_INFO)
export const onGetSellerBlockedInfoError = makeActionCreator(GET_SELLER_BLOCKED_INFO_ERROR, 'payload')
export const onGetSellerBlockedInfoSuccess = makeActionCreator(GET_SELLER_BLOCKED_INFO_SUCCESS, 'payload')
export const onGetSellerBlockedInfoThunk = payload => async dispatch => {
  const { dni = '' } = payload || {}

  dispatch(onGetSellerBlockedInfo())

  try {
    const { data } = await postBlockedInformation({
      actionType: GET_SELLER_BLOCKED_INFO,
      dni,
      isForeign: checkIfForeignDni(dni) ? 1 : 0,
      isLog: 0,
      module: 'FICHA'
    })

    dispatch(
      onGetSellerBlockedInfoSuccess({
        isBlocked: data.payload?.bloqueado,
        reason: data.payload?.motivo || ''
      })
    )
  } catch (error) {
    if (error.originalError?.code === 'ERR_CANCELED') return

    dispatch(
      onGetSellerBlockedInfoError({
        error: customizeError({
          code: error?.code,
          originalError: error?.originalError,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}

export const GET_SELLER_LEGAL_ADDRESS = 'GET_SELLER_LEGAL_ADDRESS'
export const GET_SELLER_LEGAL_ADDRESS_ERROR = 'GET_SELLER_LEGAL_ADDRESS_ERROR'
export const GET_SELLER_LEGAL_ADDRESS_SUCCESS = 'GET_SELLER_LEGAL_ADDRESS_SUCCESS'
export const onGetSellerLegalAddress = makeActionCreator(GET_SELLER_LEGAL_ADDRESS)
export const onGetSellerLegalAddressError = makeActionCreator(GET_SELLER_LEGAL_ADDRESS_ERROR, 'payload')
export const onGetSellerLegalAddressSuccess = makeActionCreator(GET_SELLER_LEGAL_ADDRESS_SUCCESS, 'payload')
export const onGetSellerLegalAddressThunk = payload => async dispatch => {
  const { dni = '' } = payload || {}

  dispatch(onGetSellerLegalAddress)

  try {
    const isForeign = checkIfForeignDni(dni)

    const { data } = await getLegalAddress({
      actionType: GET_SELLER_LEGAL_ADDRESS,
      dni,
      isForeign: isForeign ? 1 : 0
    })

    return dispatch(onGetSellerLegalAddressSuccess({ orgAddress: data?.payload?.domicilioLegal }))
  } catch (error) {
    let reason
    switch (error.code) {
      case 401:
        reason = 'UNAUTHORIZED_ERROR'
        break
      case 404:
        reason = 'DNI_DOES_NOT_MATCH_ERROR'
        break
      default:
        reason = 'SOMETHING_WENT_WRONG_ERROR'
        break
    }

    return dispatch(
      onGetSellerLegalAddressError({
        error: customizeError({
          code: error.code,
          originalError: error?.originalError,
          reason
        }).toObject()
      })
    )
  }
}

export const GET_USER_PERSONAL_INFO = 'GET_USER_PERSONAL_INFO'
export const GET_USER_PERSONAL_INFO_ERROR = 'GET_USER_PERSONAL_INFO_ERROR'
export const GET_USER_PERSONAL_INFO_SUCCESS = 'GET_USER_PERSONAL_INFO_SUCCESS'
export const onGetUserPersonalInfo = makeActionCreator(GET_USER_PERSONAL_INFO)
export const onGetUserPersonalInfoError = makeActionCreator(GET_USER_PERSONAL_INFO_ERROR, 'payload')
export const onGetUserPersonalInfoSuccess = makeActionCreator(GET_USER_PERSONAL_INFO_SUCCESS, 'payload')
export const onGetUserPersonalInfoThunk = payload => async dispatch => {
  const { dni = '' } = payload || {}

  dispatch(onGetUserPersonalInfo())

  try {
    const { data } = await postGetUserInfo({ dni })

    const userInfo = {
      circumscriptionBirth: data.payload.circunscripcionNacimiento,
      dateBirth: data.payload.fechaNacimiento,
      dateOfDeathPresumed: data.payload.fechaDefuncion,
      dv: data.payload.dv,
      gender: data.payload.sexo,
      maritalStatus: data.payload.estadoCivil,
      maternalSurname: data.payload.apellidoMaterno,
      names: data.payload.nombres,
      nationality: data.payload.nacionalidad,
      number: data.payload.numero,
      paternalSurname: data.payload.apellidoPaterno,
      profession: data.payload.profesion
    }

    dispatch(onGetUserPersonalInfoSuccess(userInfo))
  } catch (error) {
    if (error.originalError?.code === 'ERR_CANCELED') return

    dispatch(
      onGetUserPersonalInfoError({
        error: customizeError({
          code: error?.code,
          originalError: error?.originalError,
          reason: 'SOMETHING_WENT_WRONG_ERROR'
        }).toObject()
      })
    )
  }
}
